import { format } from 'date-fns';
import { FC, useEffect, useRef, useState } from 'react';

import MessageForm from './components/MessageForm/MessageForm';
import MessagesLoader from './components/MessagesLoader/MessagesLoader';
import { useMessages } from './contexts/MessagesContext';
import {
  ActionMessageSenderNameStyled,
  ActionMessageSentDateStyled,
  ActionMessageStyled,
  AssignToWCToggleWrapStyled,
  AttachmentNameStyled,
  CountdownStyled,
  FlexContainerStyled,
  MessageContentStyled,
  MessageOptionsWrapStyled,
  MessageSenderNameStyled,
  MessageSentDateStyled,
  MessageStyled,
  MessageWrapStyled,
  MessagesWrapStyled,
} from './Messages.styled';
import Avatar from 'components/Avatar/Avatar';
import Button from 'components/Button/Button';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Toggle from 'components/Toggle/Toggle';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { MessageTypesE } from 'declarations/enums/messages';
import { TicketActionTypesE } from 'declarations/enums/tickets';
import { UserTypesE } from 'declarations/enums/users';
import { TicketDetailsI } from 'declarations/interfaces/tickets';
import { NoopT } from 'declarations/types/common';
import { getUserPlaceholderName } from 'helpers/user';

export type MessagesPropsT = {
  roofTopName: string;
  requiresAttachment: TicketDetailsI['requiresAttachment'];
  handleOpenAddAttachmentPopup: NoopT;
  onAssignToWarrCloudChanged: (checked: boolean) => void;
  isTicketClosed: boolean;
};

const Messages: FC<MessagesPropsT> = ({
  roofTopName,
  requiresAttachment,
  handleOpenAddAttachmentPopup,
  onAssignToWarrCloudChanged,
  isTicketClosed,
}) => {
  const { isMobile } = useResponsive();
  const { userProfile } = useUserProfile();
  const {
    messages,
    isLoadingMessages,
    isCreatingMessage,
    undoMessageTimeLeft,
    unsavedMessage,
    handleUndoMessage,
  } = useMessages();
  const messagesWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesWrapperRef.current && (messages.length || unsavedMessage)) {
      const { current: messagesWrapper } = messagesWrapperRef;

      messagesWrapper.scrollTop = messagesWrapper.scrollHeight;
    }
  }, [messages, unsavedMessage]);

  const [assignToWarrCloud, setAssignToWarrCloud] = useState<boolean>(true);

  const handleAssignToWarrCloudChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAssignToWarrCloud(event.target.checked);
    onAssignToWarrCloudChanged(event.target.checked);
  };
  const handleUrlPreviewClick = (imageUrl: string) => {
    window.open(imageUrl, '_blank', 'noopener, noreferrer');
  };

  return (
    <>
      <MessagesWrapStyled ref={messagesWrapperRef}>
        {isLoadingMessages || isCreatingMessage ? (
          <MessagesLoader />
        ) : (
          messages.map(
            (
              {
                action,
                typeId,
                sender,
                sentDate,
                text,
                attachmentName = '',
                attachmentText = '',
                attachmentUrl = '',
              },
              i
            ) => {
              const [freeText] = text.split('{');
              const isWarrCloudUser = sender.typeId === UserTypesE.WarrCloud;
              const senderDisplayName = isWarrCloudUser ? 'WarrCloud' : sender.displayName;
              const [senderFirstName, senderLastName] = senderDisplayName
                ? senderDisplayName.split(' ')
                : [];
              const isLoggedInUser = sender.userId === userProfile?.id;
              const isTextMessage = typeId === MessageTypesE.Text;
              const isAttachmentTypeMessage =
                typeId === MessageTypesE.ArtifactUpload ||
                typeId === MessageTypesE.ArtifactDelete ||
                typeId === MessageTypesE.AttachmentApproval ||
                typeId === MessageTypesE.ScreenshotUpload;
              const isGetApprovalTypeMessage =
                typeId === MessageTypesE.Action &&
                action?.typeId === TicketActionTypesE.GetApproval &&
                action?.privateMessage;

              const isActionMessage = typeId !== MessageTypesE.Text;
              const hasThumbnail = attachmentUrl.length > 0;

              return (
                <MessageWrapStyled
                  data-testid="message"
                  key={`message-${i}`}
                  justify={isActionMessage ? 'center' : isLoggedInUser ? 'end' : 'start'}
                  align="start"
                >
                  {!isActionMessage && !isLoggedInUser && (
                    <Avatar
                      size="md"
                      placeholder={getUserPlaceholderName(senderFirstName, senderLastName)}
                      imageUrl={sender.photoUrl}
                    />
                  )}
                  <FlexContainerStyled
                    direction="column"
                    align="center"
                    isLoggedInUser={isLoggedInUser}
                    hasThumbnail={hasThumbnail}
                  >
                    {isActionMessage && (
                      <ActionMessageSentDateStyled size="xs">
                        {format(new Date(sentDate), 'p, MMM d, yyyy')}
                      </ActionMessageSentDateStyled>
                    )}
                    <MessageContentStyled
                      isActionMessageContent={isActionMessage}
                      isLoggedInUser={isLoggedInUser}
                      hasThumbnail={hasThumbnail}
                    >
                      {!isActionMessage ? (
                        <>
                          <FlexContainer align="start">
                            <MessageSenderNameStyled>
                              {!isLoggedInUser && (
                                <Typography
                                  variant="body2"
                                  isInline={true}
                                  color={isWarrCloudUser ? 'purple' : undefined}
                                >
                                  {senderDisplayName}
                                </Typography>
                              )}
                            </MessageSenderNameStyled>
                            <MessageSentDateStyled size="xs">
                              {format(new Date(sentDate), 'p, MMM d, yyyy')}
                            </MessageSentDateStyled>
                          </FlexContainer>
                          <MessageStyled
                            size="lg"
                            isText={isTextMessage}
                            isLoggedInUser={isLoggedInUser}
                          >
                            {text}
                            <AttachmentNameStyled>
                              {action?.involvedUser ? (
                                action.involvedUser.typeId === UserTypesE.WarrCloud ? (
                                  <Typography variant="body2" isInline={true} color="purple">
                                    WarrCloud
                                  </Typography>
                                ) : (
                                  action.involvedUser.displayName
                                )
                              ) : undefined}
                            </AttachmentNameStyled>
                          </MessageStyled>
                        </>
                      ) : (
                        <>
                          <FlexContainer align="center" direction={hasThumbnail ? 'column' : 'row'}>
                            {!isLoggedInUser && (
                              <ActionMessageSenderNameStyled variant="body2">
                                <Typography
                                  variant="body2"
                                  isInline={true}
                                  color={isWarrCloudUser ? 'purple' : undefined}
                                >
                                  {senderDisplayName}
                                </Typography>
                              </ActionMessageSenderNameStyled>
                            )}
                            {isAttachmentTypeMessage ? (
                              <ActionMessageStyled
                                size="lg"
                                hasThumbnail={hasThumbnail}
                                isLoggedInUser={isLoggedInUser}
                              >
                                {typeId !== MessageTypesE.ScreenshotUpload ? freeText : null}
                                {attachmentUrl && (
                                  <img
                                    className="image-preview"
                                    src={attachmentUrl}
                                    alt={attachmentName}
                                    onClick={() => handleUrlPreviewClick(attachmentUrl)}
                                    style={{ maxHeight: '120px', maxWidth: '100%' }}
                                  />
                                )}
                                <AttachmentNameStyled>{attachmentName}</AttachmentNameStyled>
                                {attachmentText}
                              </ActionMessageStyled>
                            ) : (
                              <>
                                <ActionMessageStyled size="lg">{text}</ActionMessageStyled>
                              </>
                            )}
                          </FlexContainer>
                          {isGetApprovalTypeMessage && (
                            <ActionMessageStyled size="lg">
                              <AttachmentNameStyled>{action.privateMessage}</AttachmentNameStyled>
                            </ActionMessageStyled>
                          )}
                        </>
                      )}
                    </MessageContentStyled>
                  </FlexContainerStyled>
                </MessageWrapStyled>
              );
            }
          )
        )}

        {unsavedMessage && (
          <MessageWrapStyled data-testid="unsaved-message" justify="between" align="start">
            <FlexContainer>
              <MessageContentStyled align="start">
                <div>
                  <FlexContainer justify="start">
                    <MessageSentDateStyled size="xs">
                      {format(unsavedMessage.sentDate, 'p, MMM d, yyyy')}
                    </MessageSentDateStyled>
                  </FlexContainer>
                  <MessageStyled size="lg" isLoggedInUser>
                    {unsavedMessage.text}
                  </MessageStyled>
                </div>
              </MessageContentStyled>
            </FlexContainer>

            <FlexContainer direction={isMobile ? 'column-reverse' : 'row'} align="center">
              <CountdownStyled size="xs">{undoMessageTimeLeft} sec</CountdownStyled>
              <Button
                aria-label="Undo message"
                variant="outline-rounded"
                size="xs"
                onClick={handleUndoMessage}
              >
                Undo
              </Button>
            </FlexContainer>
          </MessageWrapStyled>
        )}
      </MessagesWrapStyled>

      <MessageOptionsWrapStyled align="center" justify="end">
        <AssignToWCToggleWrapStyled>
          <Toggle
            name="assignToWarrCloud"
            defaultChecked={assignToWarrCloud}
            onChange={handleAssignToWarrCloudChange}
          />
        </AssignToWCToggleWrapStyled>
        <Typography variant="body3">Assign to WarrCloud</Typography>
      </MessageOptionsWrapStyled>

      <MessageForm
        requiresAttachment={requiresAttachment}
        roofTopName={roofTopName}
        assignToWarrCloud={assignToWarrCloud}
        handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
        isTicketClosed={isTicketClosed}
      />
    </>
  );
};

export default Messages;
